import type {
  Comment,
  Language,
  Werkverzameling,
  WerkverzamelingReason,
  Visibility,
} from '@eformulieren/engine'
import { fieldVisible, laagOf } from '@eformulieren/engine'
import type {
  CriterionOutcome,
  Projection,
  ProjectionSection,
  Field,
  FieldId,
  ViewMode,
} from '@eformulieren/model'
import { Fragment, type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import type { PresenceSource } from '../collab/PresenceBar.js'
import { inLanguage, localeOf } from '../language/text.js'
import { type FormPanels, type PanelCount, PanelFrame, labelWithCount, usePhoneWidth } from './RightPanel.js'
import { focusInField } from './focusInField.js'
import { type CriteriaState, type SectionProgress, TableOfContents } from './TableOfContents.js'
import { type OutstandingEntry, OutstandingOverview } from './OutstandingOverview.js'
import { type SectionAdvice, SectionPanel, WHOLE_SECTION } from './SectionPanel.js'
import type { AdviceTarget } from './adviceModel.js'
import type { ShareAnchor } from './shareModel.js'
import type { QuestionHistoryEntry } from './history.js'
import { MODE_NAME, MODE_MILESTONE, modeBuilt } from './mode.js'
import { numbersOf } from './numbering.js'
import { useFollowSticky } from './useFollowSticky.js'

/**
 * Het formulier zoals de invuller het ziet.
 *
 * Twee regels uit de brief lopen hier door alles heen: de renderer tekent een per
 * gebruiker gefilterde projectie en nooit de volledige definitie (R-01a), en de
 * weergavemodus is een prop (R-138a, B7). Wat een sectie mag staat in de projectie
 * (`recht`), wat zichtbaar is rekent de engine uit; de renderer leidt geen van
 * beide zelf af.
 *
 * **Geen wizard en geen blokkade op volgorde** (R-07). De invuller werkt van boven
 * naar beneden of in elke andere volgorde. De voorwaardenstatus loopt live mee
 * (R-24, M2.4) maar stuurt alleen het indienen, nooit de navigatie.
 *
 * De contextkop met dossier, ronde, call en deadline (R-125, R-126, R-128) komt in
 * M2 als prop binnen. Waar die context vandaan komt is met het vervallen van B8
 * veranderd: niet meer over een kanaal van een gastheer, maar uit de instantie zelf
 * en uit het token van de eigen inlogdienst (ontwerp 3.4). Dat blijft een zaak van
 * de aanroeper en niet van deze component.
 */

export interface FormProps {
  readonly projectie: Projection
  readonly mode: ViewMode
  readonly taal: Language
  /** Wat zichtbaar is, uitgerekend door de engine (R-33, R-34). */
  readonly visibility: Visibility
  /**
   * Hoe een veld wordt getekend. De sectie-id gaat mee, want een control moet
   * weten in welk sectiedocument hij schrijft (ontwerp 6.8). Het vierde argument zijn de id's
   * van de markeringen die `SectionPanel` al naast het veld heeft getekend, bedoeld voor
   * `FieldShell`'s `extraDescription` (WCAG 1.3.1, M8.12).
   */
  /** De naam van een bronblok voor de gedeelde bronregel (R-58); zie `SectionPanel`. */
  readonly sourceName?: (contract: string) => string
  readonly renderField: (
    field: Field,
    sectionNumber: number | undefined,
    sectionId: string,
    markerIds: readonly string[],
    /** Of dit een overgenomen veld is onder een gedeelde bronregel (R-58): dan geen eigen markering. */
    inSourceGroup?: boolean,
  ) => ReactNode
  /**
   * De alleen-lezen contextkop (R-125, R-126, R-128): een prop, gevuld door `Invulformulier.tsx`
   * met `Contextkop.tsx` (titel, dossier, uitvraagmoment, deadline en de recordnavigator).
   *
   * Draagt hij de titel al, dan tekent dit component zijn eigen `<h1>` niet nog een keer: die
   * zou dan los van de kaart komen te staan waar hij canvasmatig (`Main.dc.html`) juist bij hoort
   * (`ef-contextkop-en-titel.test.tsx`). Ontbreekt deze prop (de directe testaanroepen die geen
   * `ContextHeader` gebruiken), dan valt dit component terug op zijn eigen titel hieronder, zodat de
   * titel altijd zichtbaar blijft.
   */
  readonly contextHeader?: ReactNode
  /** Het label van de inhoudsopgave, in beide talen aangeleverd door de gastheer-schil. */
  readonly tableOfContentsLabel?: string
  /**
   * Wat deze persoon bij deze overdracht moet bekijken (R-176), zoals de pod hem levert.
   *
   * Alleen betekenisvol in de modi `aanvullen` en `toetsen`; in de andere modi is er geen
   * overdracht om zich tegen af te zetten. Ontbreekt hij, dan tekent het formulier zich als
   * altijd: geen badges, geen focus, en dat is de juiste val-terug voor een lezer die de
   * werkverzameling niet heeft opgehaald.
   */
  readonly werkverzameling?: Werkverzameling
  /** De opmerkingen van NWO, op de sectie waar ze over gaan (R-75). */
  readonly comments?: readonly Comment[]
  /**
   * De velden waarvan het antwoord anders is dan in de vorige beurt (R-178a).
   *
   * Geprojecteerd aangeleverd door de pod: wat deze lezer niet mag zien komt niet als verschil
   * in beeld (R-179), en de pod is de enige die de vorige stand heeft.
   */
  readonly changedFields?: readonly string[]
  /** De velden die deze sessie zijn voorgevuld vanuit de querystring (R-37). */
  readonly prefilledFields?: readonly string[]
  /**
   * De sectie van de laatste invoer, eenmalig te openen bij het opnieuw openen (R-164).
   *
   * Van `Invulformulier`, dat het veld-id uit `localStorage` al naar een sectie heeft
   * vertaald: dit skelet hoeft `localStorage` niet te kennen, alleen `kies` opnieuw te doen
   * met een sectie die het toch al kent. Verandert de waarde (een sprong naar een andere
   * instantie, elk met zijn eigen laatste invoer), dan springt het formulier opnieuw; blijft
   * hij gelijk, dan gebeurt er niets extra's.
   */
  readonly beginSection?: string
  /** De uitslag per criterium van de vorige beurt, alleen voor een toetser (R-178a). */
  readonly previousOutcomes?: readonly CriterionOutcome[]
  /**
   * De eerdere versies van deze aanvraag, oudste eerst (R-174).
   *
   * Leeg of weggelaten betekent: er zijn er geen, en dan staat er geen kiezer. Dat is de
   * beginstand van een eerste beurt en geen fout; de pod levert de lijst over de hele keten en
   * die is dan leeg.
   */
  readonly earlierVersies?: readonly EarlierVersie[]
  /** De antwoorden van de gekozen eerdere versie, per veld (R-96). */
  readonly eerdereWaarden?: ReadonlyMap<string, unknown>
  /** De kop bij die antwoorden; standaard "Eerder ingediend" (`EarlierValue`). */
  readonly eerdereWaardenLabel?: Readonly<Record<'nl' | 'en', string>>
  /** De eerdere antwoorden per criterium, in de modus met historie (R-137); zie `SectionPanel`. */
  readonly historie?: ReadonlyMap<string, readonly QuestionHistoryEntry[]>
  /** Welke versie nu naast de huidige staat, of niets. */
  readonly chosenVersie?: string
  /**
   * De invuller kiest een eerdere versie, of niets meer.
   *
   * De antwoorden komen van buiten en niet van hier: alleen de pod kan ze door de projectie van
   * deze lezer halen (R-179), en de renderer heeft geen weg naar een revisie.
   */
  readonly onEarlierVersie?: (revisieId: string | undefined) => void
  /**
   * Wie er nu per sectie werkt (R-72), of niets.
   *
   * Een functie en geen map: de schil heeft de verbindingen en weet welke awareness bij welke
   * sectie hoort; dit skelet hoeft er alleen naar te vragen. Zonder deze prop staat er geen
   * presence-balk en werkt het formulier gewoon.
   */
  readonly presencePer?: (sectionId: string) => PresenceSource | undefined
  /**
   * Hoeveel opmerkingdraadjes er per sectie nog openstaan (R-73), of niets.
   *
   * Een functie en geen record, om dezelfde reden als `presencePer`: de bron staat in de
   * documenten en de schil hoort er niet op te wachten. Ontbreekt hij, dan staat er nergens een
   * getal, en dat is de juiste val-terug voor een lezer zonder samenwerklaag.
   */
  readonly outstandingPer?: (sectionId: string) => number
  /**
   * Hoeveel harde controles er per sectie nog openstaan (`evaluate()`'s `status.sectie`), voor
   * de statusiconen en de voortgangsbalk in de inhoudsopgave (c1, c2, R-157). Zie
   * `Inhoudsopgave.tsx` voor waarom dit een functie is en niet een record.
   */
  readonly hardChecksPer?: (sectionId: string) => number | undefined
  /**
   * Hoeveel verplichte velden er per sectie zijn ingevuld, en van hoeveel, voor de neutrale teller
   * in de inhoudsopgave (ontwerp van het invulscherm, 29 oktober 2026). Zie `TableOfContents`.
   */
  readonly progressPer?: (sectionId: string) => SectionProgress | undefined
  /**
   * Hoe een deelnemer in de presence-balk van een sectie heet (R-72), of niets.
   *
   * Van `FillForm`, dat de nummering over alle secties heen maakt, zodat "Deelnemer 2" in elke
   * sectie en in de kopbalk dezelfde persoon is. Zie `PresenceBar`.
   */
  readonly presenceName?: (person: string) => string
  /** De eigen persoonssleutel, zodat een tweede venster van de lezer nooit als een ander staat (G-18). */
  readonly presenceSelf?: string
  /**
   * De actiebalk onderaan, met de navigatie naar de volgende sectie (ontwerp van het invulscherm).
   *
   * Een render-prop en geen kant-en-klare balk: de inhoud (indienen, de toetsknoppen) is van
   * `FillForm`, maar welke sectie nu actief is en hoe je naar de volgende springt is van dit
   * skelet (`useActiveSection`). De balk staat binnen `.ef-formulier`, zodat hij onderaan het
   * formulier blijft plakken tijdens het scrollen en ophoudt waar het formulier ophoudt.
   */
  readonly renderFooter?: (navigation: SectionNavigation) => ReactNode
  /**
   * Per laag een lijst die na de laatste zichtbare sectie van die laag staat (R-46, R-50): de
   * literatuurlijst van een laag zonder geplaatste referentielijst. Van `FillForm`, dat de citaties
   * kent; deze schil weet alleen waar de laag ophoudt. In de toetsgedaante na het paar waarin die
   * laatste sectie staat, en niet in de oordeelkaart.
   */
  readonly laagLijsten?: readonly { readonly laag: 'invoer' | 'toets'; readonly node: ReactNode }[]
  /**
   * Of bovenaan de zin staat dat velden met een sterretje verplicht zijn (besluit van 1 november 2026,
   * ontwerp 10.3, R-09). Van `FillForm`: alleen als deze lezer verplichte velden heeft om in te
   * vullen, dus alleen als er ergens een sterretje staat; anders legt de zin iets uit wat er niet is.
   */
  readonly requiredNote?: boolean
  /**
   * Per te toetsen sectie de stand van haar criteria, voor de inhoudsopgave van de toetser
   * (ontwerp van het toetsscherm, R-98). Van `FillForm`, dat de uitkomsten heeft.
   */
  readonly criteriaPer?: (sectionId: string) => CriteriaState | undefined
  /** Hoeveel criteria van deze lezer een oordeel hebben, en van hoeveel. */
  readonly criteriaProgress?: { readonly beoordeeld: number; readonly totaal: number }
  /**
   * Het overzicht van wat er nog openstaat (R-24, R-140), of niets.
   *
   * `regels` komt van `Invulformulier`, dat de status uit `evaluate` al heeft; dit skelet rekent
   * er niets aan uit. `verzoek` telt hoe vaak de invuller erom vroeg: nul betekent dat het
   * overzicht er nog niet staat, en elke ophoging klapt de secties met een open controle open en
   * geeft de kop van het overzicht de focus. Een teller en geen boolean, zodat een tweede klik op
   * "Controleren en indienen" de invuller opnieuw naar het overzicht brengt; de schakelaar in de kop
   * doet het overzicht dan juist dicht (`open`).
   *
   * Het overzicht staat hier en niet in `Invulformulier`, omdat een link alleen werkt als hij de
   * sectie kan openklappen, en de open- en dichtstand van de secties is van dit skelet.
   *
   * `open` onwaar haalt het overzicht weg zonder de teller terug te zetten, en `onSluiten` geeft het een
   * knop Sluiten en Escape (live test van 3 november 2026); of het open staat en waar de focus daarna
   * heen gaat, houdt de aanroeper bij, want die heeft de knoppen die het openen. `id` is voor hun
   * `aria-controls`.
   */
  readonly outstanding?: {
    readonly regels: readonly OutstandingEntry[]
    readonly verzoek: number
    readonly fout?: boolean
    readonly indienen?: boolean
    readonly open?: boolean
    readonly id?: string
    readonly onSluiten?: () => void
  }
  /**
   * Advies bij de vragen en sectiekoppen (R-184, ontwerp 7.4a): de tekstballon waar de kijker kan
   * vragen, het label "1 advies" bij een plek met een verzoek, en een vaste markering op de
   * adviseurpagina. Zonder deze prop staat er niets van advies (R-184b).
   */
  readonly advies?: AdviceMarks
  /**
   * Een paneel rechts naast het formulier: het adviespaneel (R-184, mockup 01). Op een breed formulier
   * een kolom van `shell-internal.sidebar.width`; smaller dan 78rem een tweede weergave onder de kop,
   * met twee knoppen Formulier en het label van `knop`. Zonder deze prop geen kolom en geen knoppen.
   */
  readonly zijpaneel?: SidePanel
  /**
   * De panelen van het formulier zelf, Bronnen, Opmerkingen en Geschiedenis, in hetzelfde gebied als
   * het adviespaneel (`RightPanel.tsx`). Een open paneel staat op een breed formulier in de kolom op de
   * plek van het adviespaneel, daaronder als tweede weergave achter zijn knop, en op een telefoon als
   * blad over het hele scherm. Welk paneel open is houdt `FillForm` bij, want de knoppen staan in de kop.
   */
  readonly panelen?: FormPanels
  /**
   * Een banner boven het formulier, vóór de knoppen van de tweede weergave: de uitleg voor wie via een
   * deling binnenkwam (`SharedEntryBanner`, mockup 07) of de melding dat de toegang is gewijzigd.
   */
  readonly banner?: ReactNode
  /** "lezen" of "schrijven" per sectie in de inhoudsopgave (mockup 07); zie `TableOfContents`. */
  readonly rechtZichtbaar?: boolean
  /** Per sectie of ze als leessectie wordt aangeduid, en of er een opmerking te plaatsen is; zie `SectionPanel`. */
  readonly alleenLezenPer?: (sectionId: string) => { readonly opmerkingen: boolean } | undefined
  /** Wat er naast de klapknop van een sectiekop staat (de ballon voor een opmerking, R-73). */
  readonly kopExtraPer?: (section: ProjectionSection) => ReactNode
  /** Wat bovenaan in een sectie staat, onder de kop: de opmerkingen bij de sectie (R-73). */
  readonly bovenaanPer?: (section: ProjectionSection) => ReactNode
}

/** Advies in het formulier; zie {@link FormProps.advies}. */
export interface AdviceMarks {
  /** Of de kijker advies kan vragen (`kanVragen`): dan staat de tekstballon er. */
  readonly kanVragen: boolean
  /** Secties waarover de definitie geen advies toelaat: daar geen ballon. */
  readonly uitgesloten?: readonly string[]
  /** Hoeveel verzoeken precies deze plek als anker hebben (`adviceCountFor`). */
  readonly aantal: (target: AdviceTarget) => number
  /** De dialoog openen met dit anker. */
  readonly onVragen: (anchor: ShareAnchor) => void
  /** Het paneel openen op de verzoeken van deze plek. */
  readonly onToon: (target: AdviceTarget) => void
  /** Altijd gemarkeerd, met de tekst "Hierover gaat de vraag": het anker op de adviseurpagina. */
  readonly vast?: AdviceTarget
}

/** Het paneel naast het formulier; zie {@link FormProps.zijpaneel}. */
export interface SidePanel {
  /** Het label van de tweede weergave op een smal scherm, zonder aantal: "Advies". */
  readonly knop: string
  /** Het aantal naast het label, als vak, en hoe het in de naam klinkt ("Advies: 3 verzoeken"). */
  readonly teller?: PanelCount
  /** Telt op bij elke vraag om het paneel te tonen; op een smal scherm wisselt de weergave dan. */
  readonly toon: number
  readonly render: (context: SidePanelContext) => ReactNode
  /**
   * Wat er sinds de vorige lezing nieuw is ("Nieuw advies van …"). Het formulier zet het in één
   * `aria-live=polite`-regio buiten het paneel, want op een smal scherm is het paneel in de weergave
   * Formulier verborgen, en een verborgen live regio meldt niets (WCAG 4.1.3).
   */
  readonly melding?: string
}

/** Wat het paneel van het formulier krijgt. */
export interface SidePanelContext {
  /** De sectie waar de lezer nu is, voor "Deze sectie". */
  readonly actieveSectie: string | undefined
  /** Naar een vraag of sectie: openklappen, markeren, de focus erin, en op een telefoon naar Formulier. */
  readonly springNaar: (target: AdviceTarget) => void
}

/** Wat de actiebalk van de sectienavigatie weet en kan (`FormProps.renderFooter`). */
export interface SectionNavigation {
  /** De sectie waar de invuller nu is, of niets als er geen zichtbare sectie is. */
  readonly active: string | undefined
  /** De volgende zichtbare sectie, of niets bij de laatste. */
  readonly next: string | undefined
  /** Naar een sectie: openklappen, ernaartoe scrollen en de focus op haar kop (R-142). */
  readonly goTo: (sectionId: string) => void
}

/**
 * Eén eerdere versie in de kiezer, zoals `GET /revisies` hem levert (R-174).
 *
 * Dezelfde vorm als daar, en met opzet niet het hele revisieschema uit het model: de renderer
 * heeft de antwoorden hier niet nodig (die komen per gekozen versie) en zou ze met een schema
 * erbij wel binnenkrijgen.
 */
export interface EarlierVersie {
  readonly id: string
  readonly volgnummer: number
  readonly gebeurtenis: string
  readonly moment: string
}

/**
 * Zoals `GET /werkverzameling` hem levert (R-75), en zoals het voorbeeld van de configurator
 * hem zelf vastlegt (`packages/engine/src/voorbeeld.ts`). Eén type in de engine, hier
 * doorgegeven zodat een bestaande import van dit bestand blijft werken.
 */
export type { Comment }

export function Form({
  projectie: projection,
  mode,
  taal: language,
  visibility,
  renderField,
  sourceName,
  contextHeader,
  tableOfContentsLabel,
  werkverzameling,
  comments,
  changedFields,
  prefilledFields,
  beginSection,
  previousOutcomes,
  earlierVersies,
  eerdereWaarden: earlierValues,
  eerdereWaardenLabel: earlierLabel,
  historie: history,
  chosenVersie,
  onEarlierVersie,
  presencePer,
  outstandingPer,
  hardChecksPer,
  progressPer,
  presenceName,
  presenceSelf,
  renderFooter,
  laagLijsten: layerLists = [],
  requiredNote = false,
  criteriaPer,
  criteriaProgress,
  outstanding,
  advies: advice,
  zijpaneel: sidePanel,
  panelen: formPanels,
  banner,
  rechtZichtbaar: showRight = false,
  alleenLezenPer: readOnlyPer,
  kopExtraPer: headerExtraPer,
  bovenaanPer: topPer,
}: FormProps): ReactNode {
  /**
   * De schakelaar naar het geheel (R-35, R-132).
   *
   * Standaard staat de focus aan als de pod zegt dat er iets te focussen is; de invuller
   * schakelt hem uit en ziet dan alles. **Er wordt niets verborgen** (R-177): de projectie
   * blijft volledig en de badges per sectie blijven staan, dus wat buiten de werkverzameling
   * valt is één klik weg en niet onbereikbaar.
   */
  const [onlyWork, setOnlyWork] = useState(true)

  // De inhoudsopgave beweegt met de leesrichting mee (R-02, R-142): zie
  // `useVolgSticky` voor het waarom. `bodyRef` is het anker (niet-sticky, niet
  // getransformeerd) waartegen de natuurlijke positie van de inhoudsopgave wordt
  // gemeten; `tableOfContentsRef` volgt de knoop zelf, die pas vanaf twee secties
  // mount (`MINIMUM_SECTIONS`).
  const bodyRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const observedFooter = useRef<{ element: Element; observer: ResizeObserver } | undefined>(undefined)

  /*
   * De hoogte van de actiebalk die onderaan plakt, als `--ef-voet-ruimte` op het formulier
   * (WCAG 2.4.11, audit van 30 oktober 2026). De stylesheet geeft elk element dat focus kan
   * krijgen die ruimte als `scroll-margin-bottom`, zodat de browser een veld, knop of item van de
   * inhoudsopgave bij Tab of bij een sprong erboven schuift en niet erachter. Gemeten en niet
   * geschat: de balk is op een telefoon twee rijen hoog en in de toetsgedaante anders dan bij
   * invullen. Na elke render opnieuw bekeken, want de balk kan een ander element worden.
   */
  useLayoutEffect(() => {
    const root = rootRef.current
    const footer = root?.querySelector(':scope > .ef-schilvoet') ?? undefined
    if (observedFooter.current?.element === footer) return
    observedFooter.current?.observer.disconnect()
    observedFooter.current = undefined
    if (root === null || footer === undefined) {
      root?.style.removeProperty('--ef-voet-ruimte')
      return
    }
    const observer = new ResizeObserver(() => {
      root.style.setProperty('--ef-voet-ruimte', `${String(Math.ceil(footer.getBoundingClientRect().height))}px`)
    })
    observer.observe(footer)
    observedFooter.current = { element: footer, observer }
  })
  useEffect(() => () => observedFooter.current?.observer.disconnect(), [])

  /*
   * Het vangnet voor wat `scroll-margin` niet dekt. Een rich-text-veld dat met Tab de focus krijgt
   * schuift de browser niet naar het element maar naar de cursor erin, en dat tot net binnen de
   * onderrand van het venster: achter de voet. Komt de focus binnen het formulier op een element
   * waarvan het begin achter de voet ligt, dan schuift dit het alsnog erboven (WCAG 2.4.11). Na een
   * frame, zodat het eigen scrollen van de browser en de editor al is gebeurd.
   */
  useEffect(() => {
    const root = rootRef.current
    if (root === null) return
    const onFocus = (event: FocusEvent): void => {
      const target = event.target
      if (!(target instanceof HTMLElement)) return
      requestAnimationFrame(() => {
        if (document.activeElement !== target) return
        // Alleen bij focus van het toetsenbord: wie met de muis klikt, ziet waar hij klikt, en een
        // scroll tussen twee klikken zou de tweede op een andere plek laten landen.
        if (!target.matches(':focus-visible')) return
        const footer = root.querySelector(':scope > .ef-schilvoet')
        if (footer === null || footer.contains(target)) return
        const line = footer.getBoundingClientRect().top
        if (target.getBoundingClientRect().top < line - MINIMUM_VISIBLE) return
        target.scrollIntoView({ block: 'nearest' })
      })
    }
    root.addEventListener('focusin', onFocus)
    return () => root.removeEventListener('focusin', onFocus)
  }, [])
  const tableOfContentsRef = useRef<HTMLElement | null>(null)
  /**
   * De lucht boven de inhoudsopgave als hij plakt: 16px, en daarbovenop de compacte kop zolang die
   * bovenaan staat (`CompactHeader` in `ContextHeader.tsx`, besluit van 2 november 2026), anders schoof
   * de kop van de inhoudsopgave eronder.
   */
  const tableOfContentsTop = useCallback((): number => belowCompactHeader(rootRef.current), [])
  useFollowSticky(tableOfContentsRef, bodyRef, { topGap: tableOfContentsTop })

  const reasons = useMemo(() => {
    const card = new Map<string, WerkverzamelingReason>()
    for (const section of werkverzameling?.secties ?? []) card.set(section.sectieId, section.reden)
    return card
  }, [werkverzameling])

  const commentPer = useMemo(() => {
    const card = new Map<string, Comment[]>()
    for (const comment of comments ?? []) {
      card.set(comment.sectieId, [...(card.get(comment.sectieId) ?? []), comment])
    }
    return card
  }, [comments])

  const changed = useMemo(() => new Set(changedFields ?? []), [changedFields])
  const prefilled = useMemo(() => new Set(prefilledFields ?? []), [prefilledFields])

  /**
   * De naam van elke vraag, voor een criterium dat er één aanwijst (D-55).
   *
   * Hier en niet in het sectiepaneel: de vraag waarover een criterium gaat staat in een andere
   * sectie dan het criterium zelf, dus alleen deze schil kan hem opzoeken. Een veld zonder label
   * komt er niet in en dan valt het paneel terug op het id; dat is een geplaatst component
   * (R-46), en die is geen vraag om over te oordelen.
   */
  const questionNames = useMemo(() => {
    const card = new Map<string, string>()
    for (const section of projection.secties) {
      for (const field of section.velden) {
        if (field.label !== undefined) card.set(field.id, inLanguage(field.label, language))
      }
    }
    return card
  }, [projection.secties, language])

  const resultPer = useMemo(() => {
    const card = new Map<string, CriterionOutcome>()
    for (const outcome of previousOutcomes ?? []) card.set(outcome.criteriumId, outcome)
    return card
  }, [previousOutcomes])

  /**
   * Kan deze weergave focussen op de werkverzameling? Bij aanvullen (R-35, R-132) en bij een
   * hertoets (R-189, ontwerp 7.5), en alleen als er iets is.
   *
   * Bij toetsen hangt het aan `gefilterd` van de pod: bij een eerste toets is dat false (de regel
   * van `ingediend` is `alles`), dus daar krijgt de toetser het hele formulier en alleen de
   * badges. Pas bij een hertoets staat er iets voorop. Hetzelfde patroon als bij aanvullen: wat
   * voorop staat is open, de rest is ingeklapt en één klik weg (R-177).
   */
  const canFocus =
    (mode === 'aanvullen' || mode === 'toetsen') && werkverzameling?.gefilterd === true && reasons.size > 0
  const focused = canFocus && onlyWork

  const visibleSections = useMemo(
    () => projection.secties.filter((section) => visibility.secties.has(section.id)),
    [projection.secties, visibility],
  )

  /**
   * De orde waarin de secties staan.
   *
   * In de modus `toetsen` staat elke toetssectie direct achter de sectie waarover ze een
   * uitspraak doet (`hoortBij`, R-109, D-55): dat is wat 7.6 "de toetslaag ertussen geweven"
   * noemt, en het is de enige orde waarin de toetser zijn oordeel naast de vraag ziet. In de
   * andere modi blijft de orde van de projectie staan, want die is de orde van de definitie.
   */
  const sections = useMemo(
    () => (mode === 'toetsen' ? interleaved(visibleSections) : visibleSections),
    [mode, visibleSections],
  )

  /**
   * Staat deze sectie voorop?
   *
   * In de toetsgedaante volgt een toetssectie de sectie die ze toetst (`hoortBij`): ze staan als
   * paar naast elkaar (R-109), en een paar dat half ingeklapt is laat de toetser oordelen over een
   * vraag die hij niet ziet. Een criterium zonder oordeel in een ingeklapt paar raakt niet zoek:
   * het overzicht van wat openstaat noemt het, en de sprong ernaartoe zet de focus uit (`openFor`).
   */
  const inFocus = useCallback(
    (section: ProjectionSection) =>
      reasons.has(section.id) ||
      (mode === 'toetsen' && section.type === 'toets' && section.hoortBij !== undefined && reasons.has(section.hoortBij)),
    [mode, reasons],
  )

  // Alles open bij openen: geen wizard, geen verstopte inhoud (R-07). Wie een
  // sectie dichtklapt houdt dat, want het is zijn keuze.
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set())
  const transition = useCallback((sectionId: string) => {
    setClosed((previous) => {
      const next = new Set(previous)
      if (!next.delete(sectionId)) next.add(sectionId)
      return next
    })
  }, [])

  const { actief: active, register: signIn, choose } = useActiveSection(sections.map((section) => section.id))

  /**
   * In de toetsgedaante noemt de inhoudsopgave de secties die getoetst worden, en niet hun
   * toetssecties ernaast: die staan naast hun sectie en horen bij dezelfde regel. Staat de
   * toetser in een toetssectie, dan licht de sectie op waarover ze gaat.
   */
  const tocSections = useMemo(
    () => (mode === 'toetsen' ? sections.filter((section) => section.type !== 'toets' || section.hoortBij === undefined) : sections),
    [mode, sections],
  )
  const tocActive = useMemo(() => {
    const current = sections.find((section) => section.id === active)
    return mode === 'toetsen' && current?.type === 'toets' && current.hoortBij !== undefined ? current.hoortBij : active
  }, [mode, sections, active])

  /**
   * Naar het overzicht van wat er nog openstaat, en vandaar naar een veld (R-24, R-140).
   *
   * Een sectie moet open zijn voordat er iets in haar de focus kan krijgen: het paneel is
   * `hidden`. Bij het tonen van het overzicht gaan daarom alle secties met een open controle open,
   * en bij een sprong de sectie van dat veld. In de gefocuste weergave van `aanvullen` staan
   * secties buiten de werkverzameling dicht; staat er daar een controle open, dan gaat die focus
   * uit, net als met de schakelaar (R-177: er wordt niets verborgen dat niet één klik weg is).
   *
   * De `ref` op de regels en niet de regels in de afhankelijkheden van het effect: het effect
   * hoort te lopen als de invuller erom vraagt, niet bij elke aanslag die de lijst verandert.
   */
  const overviewHeaderRef = useRef<HTMLHeadingElement>(null)
  const outstandingRulesRef = useRef<readonly OutstandingEntry[]>([])
  outstandingRulesRef.current = outstanding?.regels ?? []
  const request = outstanding?.verzoek ?? 0
  const openFor = useCallback(
    (sectionIds: ReadonlySet<string>) => {
      setClosed((previous) => {
        const next = new Set(previous)
        for (const sectionId of sectionIds) next.delete(sectionId)
        return next
      })
      if (focused && sections.some((section) => sectionIds.has(section.id) && !inFocus(section))) setOnlyWork(false)
    },
    [focused, inFocus, sections],
  )
  const openForRef = useRef(openFor)
  openForRef.current = openFor
  useEffect(() => {
    if (request === 0) return
    openForRef.current(new Set(outstandingRulesRef.current.map((rule) => rule.sectieId)))
    overviewHeaderRef.current?.focus()
  }, [request])
  const jumpTo = useCallback(
    (rule: OutstandingEntry) => {
      // Synchroon, zodat het paneel niet meer `hidden` is op het moment dat de focus komt.
      flushSync(() => openFor(new Set([rule.sectieId])))
      const target = document.getElementById(rule.anker)
      if (target !== null) focusInField(target)
    },
    [openFor],
  )

  /** Naar een sectie die aanpassing vraagt, vanuit de band bovenaan (R-132, R-140). */
  const jumpToSection = useCallback(
    (sectionId: string) => {
      flushSync(() => openFor(new Set([sectionId])))
      const target = document.getElementById(sectionId)
      if (target !== null) focusInField(target)
    },
    [openFor],
  )

  /**
   * Het gemarkeerde anker na een sprong uit het paneel, en welke weergave er op een smal scherm staat.
   *
   * Een vraag om het paneel te tonen (`zijpaneel.toon` telt op) zet de weergave nog in dezelfde render
   * op het paneel, en niet in een effect erna: het paneel zet in zijn eigen effect de focus op een
   * kaart, en een kaart die nog verborgen is krijgt geen focus.
   */
  const [marked, setMarked] = useState<AdviceTarget | undefined>(undefined)
  const [view, setView] = useState<'formulier' | 'paneel'>('formulier')
  const [seenShow, setSeenShow] = useState(sidePanel?.toon)
  if (sidePanel?.toon !== seenShow) {
    setSeenShow(sidePanel?.toon)
    // Alleen een echte vraag om het paneel wisselt de weergave. Komt het paneel pas na de eerste render
    // binnen (de verzoeken worden geladen), dan is dat geen vraag: anders opende een toetser op een
    // smal scherm, en in de toetsgedaante tot 104rem, op het paneel in plaats van op het formulier.
    if (sidePanel !== undefined && seenShow !== undefined) setView('paneel')
  }
  // Een open paneel van het formulier: welk, en of het als blad over het scherm staat (telefoon).
  const phone = usePhoneWidth(rootRef)
  const openPanel = formPanels?.panelen.find((panel) => panel.id === formPanels.actief)
  const [seenPanelShow, setSeenPanelShow] = useState(formPanels?.toon)
  if (formPanels?.toon !== seenPanelShow) {
    setSeenPanelShow(formPanels?.toon)
    if (openPanel !== undefined) setView('paneel')
  }
  const [seenOpen, setSeenOpen] = useState(openPanel?.id)
  // Gezet door de knop van het adviespaneel, die een paneel van het formulier sluit om het advies te tonen.
  const toAdvice = useRef(false)
  if (openPanel?.id !== seenOpen) {
    setSeenOpen(openPanel?.id)
    // Dicht: terug naar het formulier, ook als het adviespaneel er nog is; behalve op weg naar het advies.
    if (openPanel === undefined && seenOpen !== undefined) setView(toAdvice.current ? 'paneel' : 'formulier')
    toAdvice.current = false
  }
  // Vraagt de pagina om het adviespaneel (een label "1 advies" bij een vraag), dan gaat een paneel van
  // het formulier dicht, want ze delen één plek.
  const closeFormPanel = formPanels?.onSluit
  const adviceShow = sidePanel?.toon
  const firstAdviceShow = useRef(adviceShow)
  useEffect(() => {
    if (adviceShow === firstAdviceShow.current) return
    firstAdviceShow.current = adviceShow
    closeFormPanel?.({ focusTerug: false })
  }, [adviceShow, closeFormPanel])
  const inColumn = openPanel !== undefined && !phone ? openPanel : undefined
  const hasColumn = sidePanel !== undefined || inColumn !== undefined
  const jumpToAnchor = useCallback(
    (target: AdviceTarget) => {
      // Synchroon: de sectie moet open en de weergave op het formulier staan voordat de focus komt.
      flushSync(() => {
        setView('formulier')
        setMarked(target)
        openFor(new Set([target.sectieId]))
      })
      const section = document.getElementById(target.sectieId)
      const field =
        target.veldId === undefined ? null : section?.querySelector<HTMLElement>(`[data-veld="${CSS.escape(target.veldId)}"]`)
      const goal = field ?? section
      if (goal === null || goal === undefined) return
      const soft = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      goal.scrollIntoView({ behavior: soft ? 'smooth' : 'auto', block: 'center' })
      if (field !== null && field !== undefined) focusInField(field)
      else section?.querySelector<HTMLElement>('.ef-sectie__knop')?.focus()
    },
    [openFor],
  )
  const excludedFromAdvice = useMemo(() => new Set(advice?.uitgesloten ?? []), [advice?.uitgesloten])
  const adviceFor = (section: ProjectionSection): SectionAdvice | undefined => {
    if (advice === undefined) return undefined
    const key = (target: AdviceTarget | undefined) =>
      target === undefined || target.sectieId !== section.id ? undefined : (target.veldId ?? WHOLE_SECTION)
    const markedKey = key(marked)
    const fixedKey = key(advice.vast)
    return {
      vragen: advice.kanVragen && !excludedFromAdvice.has(section.id),
      aantal: advice.aantal,
      onVragen: advice.onVragen,
      onToon: advice.onToon,
      ...(markedKey === undefined ? {} : { gemarkeerd: markedKey }),
      ...(fixedKey === undefined ? {} : { vast: fixedKey }),
    }
  }

  /** Vraagt deze sectie aanpassing? Openstaand criterium, al dan niet ook gewijzigd (R-98). */
  const adjustmentPer = useCallback(
    (sectionId: string) => {
      const reason = reasons.get(sectionId)
      return reason === 'openstaand-criterium' || reason === 'beide'
    },
    [reasons],
  )

  /** Naar een sectie uit de inhoudsopgave of de actiebalk: eerst open, dan erheen (R-142). */
  const goTo = useCallback(
    (sectionId: string) => {
      setClosed((previous) => {
        const next = new Set(previous)
        next.delete(sectionId)
        return next
      })
      // Buiten de werkverzameling staat een sectie in de gefocuste weergave dicht (R-35); wie er
      // zelf heen gaat, krijgt het geheel, net als bij een sprong uit het overzicht (R-177).
      const target = sections.find((section) => section.id === sectionId)
      if (focused && (target === undefined || !inFocus(target))) setOnlyWork(false)
      choose(sectionId)
    },
    [choose, focused, inFocus, sections],
  )

  // Terugkeren op de plek van de laatste invoer (R-164): eenmalig per waarde van
  // `beginSection`, dus opnieuw bij een sprong naar een andere instantie en niet bij elke
  // her-render. `kies` no-opt vanzelf als de sectie niet (meer) bestaat voor deze lezer
  // (`elementen.current.get` levert dan niets), dus een bevroren of ontoegankelijke sectie
  // geeft geen fout en geen sprong naar niets.
  useEffect(() => {
    if (beginSection !== undefined) choose(beginSection)
  }, [beginSection, choose])

  const visibleField = useCallback(
    (fieldId: FieldId) => fieldVisible(visibility, fieldId),
    [visibility],
  )

  /**
   * Eén sectie met haar paneel. In de toetsgedaante krijgt een sectie van de invoerlaag de zin dat
   * ze staat zoals de aanvrager haar indiende (ontwerp van het toetsscherm, 29 oktober 2026).
   */
  const renderSection = (section: ProjectionSection): ReactNode => {
    const reason = reasons.get(section.id)
    const ownComments = commentPer.get(section.id)
    // Naar een lokale const en niet in de spread: met `exactOptionalPropertyTypes` is een
    // prop die `undefined` mag zijn iets anders dan een prop die er niet is.
    const presence = presencePer?.(section.id)
    const outstanding = outstandingPer?.(section.id) ?? 0
    const sectionAdvice = adviceFor(section)
    const readOnly = readOnlyPer?.(section.id)
    const headerExtra = headerExtraPer?.(section)
    const top = topPer?.(section)
    // Alleen voor wie aan de interne kant staat (R-170, D-54, G029): wie het formulier extern
    // invult mag nooit weten welke velden hij niet te zien zou krijgen als hij dat wél deed, dus
    // voor hem bestaat deze verzameling niet eens. Sinds 30 oktober 2026 hangt het aan de kant van
    // de lezer en niet aan de gedaante: ook de toetser en een NWO-medewerker in een aanvulbeurt
    // zien welke velden de aanvrager niet ziet. Tot dan gold het alleen in de gedaante `intern`.
    const onlyInternal =
      mode === 'intern' || projection.kant === 'intern'
        ? new Set(
            section.velden
              .filter((field) => 'externZichtbaar' in field && field.externZichtbaar === false)
              .map((field) => field.id),
          )
        : undefined
    return (
      <div ref={signIn(section.id)} key={section.id}>
        <SectionPanel
          sectie={section}
          taal={language}
          open={!closed.has(section.id) && (!focused || inFocus(section))}
          active={section.id === active}
          onSwitch={transition}
          // Zet de definitie de nummering uit, dan geen nummers voor de vragen (R-04, besluit van 30
          // oktober 2026: standaard aan, per formulier uit te zetten in de configurator).
          numbers={projection.nummering === false ? NO_NUMBERS : numbersOf(section, visibility)}
          renderField={renderField}
          {...(sourceName === undefined ? {} : { bronNaamVan: sourceName })}
          zichtbaarVeld={visibleField}
          {...(reason === undefined ? {} : { reden: reason })}
          {...(ownComments === undefined
            ? {}
            : { comments: ownComments })}
          {...(changed.size === 0 ? {} : { changedFields: changed })}
          {...(prefilled.size === 0 ? {} : { prefilled })}
          {...(onlyInternal === undefined || onlyInternal.size === 0
            ? {}
            : { onlyInternal })}
          {...(resultPer.size === 0 ? {} : { vorigeUitslag: resultPer })}
          {...(earlierValues === undefined ? {} : { eerdereWaarden: earlierValues })}
          {...(earlierLabel === undefined ? {} : { eerdereWaardenLabel: earlierLabel })}
          {...(history === undefined ? {} : { historie: history })}
          vraagnamen={questionNames}
          {...(presence === undefined ? {} : { aanwezigheid: presence })}
          {...(presenceName === undefined ? {} : { presenceName })}
          {...(presenceSelf === undefined ? {} : { presenceSelf })}
          openstaandeOpmerkingen={outstanding}
          readOnlyNote={mode === 'toetsen' && section.type !== 'toets' && section.recht !== 'bewerken'}
          {...(sectionAdvice === undefined ? {} : { advies: sectionAdvice })}
          {...(readOnly === undefined ? {} : { alleenLezen: readOnly })}
          {...(headerExtra === undefined || headerExtra === null ? {} : { kopExtra: headerExtra })}
          {...(top === undefined || top === null ? {} : { bovenaan: top })}
        />
      </div>
    )
  }

  return (
    <div className="ef-formulier" data-modus={mode} data-zijpaneel={hasColumn ? 'ja' : undefined} lang={language} ref={rootRef}>
      {contextHeader}
      {requiredNote ? (
        <p className="ef-formulier__verplichtuitleg">
          {inLanguage(
            {
              nl: 'Velden met * zijn verplicht.',
              en: 'Fields marked * are required.',
            },
            language,
          )}
        </p>
      ) : null}
      {modeBuilt(mode) ? null : (
        // Een modus die nog geen gedrag heeft, zegt dat. De projectie is dezelfde
        // (R-138a), dus het formulier eronder werkt; wat ontbreekt is wat deze
        // weergave anders doet, en die milestone staat erbij.
        <p className="ef-formulier__modusband" role="status">
          {/*
            Een modus zonder milestone zegt dat, en noemt er geen: een afgeronde milestone noemen
            zou een belofte zijn die niemand meer kan nakomen. Dezelfde formulering als
            `Plaatshouder.tsx` gebruikt. Van M7.6 tot de groepering bij de vraag gold dat voor
            `met-historie`; vandaag draagt elke modus zonder gedrag een milestone.
          */}
          {language === 'nl'
            ? `Weergave ${MODE_NAME[mode].nl}: deze modus krijgt zijn eigen gedrag in ${
                MODE_MILESTONE[mode] ?? 'een milestone die nog niet is toegewezen'
              }. Hieronder staat de invulweergave over dezelfde projectie.`
            : `View ${MODE_NAME[mode].en}: this mode gets its own behaviour in ${
                MODE_MILESTONE[mode] ?? 'a milestone that has not been assigned yet'
              }. What you see below is the filling-in view over the same projection.`}
        </p>
      )}
      {mode === 'aanvullen' && werkverzameling !== undefined && reasons.size > 0 ? (
        <FocusBand
          language={language}
          status={
            language === 'nl'
              ? `${reasons.size} ${reasons.size === 1 ? 'sectie vraagt' : 'secties vragen'} aanpassing.`
              : `${reasons.size} ${reasons.size === 1 ? 'section needs' : 'sections need'} changes.`
          }
          listLabel={{ nl: 'Secties die aanpassing vragen', en: 'Sections that need changes' }}
          sections={sections.filter((section) => reasons.has(section.id))}
          onJump={jumpToSection}
          onlyWork={onlyWork}
          onToggle={() => setOnlyWork((only) => !only)}
          narrowLabel={{ nl: 'Toon alleen wat aanpassing vraagt', en: 'Show only what needs changes' }}
        />
      ) : null}
      {/*
        De hertoets (R-189, ontwerp 7.5): wat voorop staat bij naam, en de schakelaar naar het geheel,
        hetzelfde patroon als bij aanvullen. Alleen bij `gefilterd`, dus niet bij een eerste toets, en
        met de secties die getoetst worden en niet hun toetssecties: die staan als paar ernaast.
      */}
      {mode === 'toetsen' && canFocus ? (
        <FocusBand
          language={language}
          status={reviewFocusStatus(
            tocSections.flatMap((section) => {
              const reason = reasons.get(section.id)
              return reason === undefined ? [] : [reason]
            }),
            language,
          )}
          listLabel={{ nl: 'Secties die open staan', en: 'Sections that are open' }}
          sections={tocSections.filter((section) => reasons.has(section.id))}
          onJump={jumpToSection}
          onlyWork={onlyWork}
          onToggle={() => setOnlyWork((only) => !only)}
          narrowLabel={{ nl: 'Toon alleen wat aandacht vraagt', en: 'Show only what needs attention' }}
        />
      ) : null}
      {/*
        De kiezer voor een eerdere versie (R-96, R-174).

        Hij staat er zodra er eerdere versies zijn, en niet alleen bij `aanvullen`: R-174 zegt
        dat eerdere revisies in te zien zijn door de betrokkenen aan beide kanten, en de lijst is
        bij een eerste beurt leeg. Zo regelt de stand zichzelf en staat er geen moduslogica die
        met de pod uit de pas kan lopen.

        Een keuzelijst en geen tweede formulier ernaast: het gekozen antwoord staat bij de vraag
        waar het over gaat (`EarlierValue`), en dat is de vorm die met een schermlezer werkt.
      */}
      {(earlierVersies?.length ?? 0) > 0 && onEarlierVersie !== undefined ? (
        <div className="ef-formulier__versies">
          <label className="ef-formulier__versielabel" htmlFor="ef-eerdere-versie">
            {inLanguage({ nl: 'Vergelijk met een eerdere versie', en: 'Compare with an earlier version' }, language)}
          </label>
          <select
            id="ef-eerdere-versie"
            className="ef-invoer ef-formulier__versiekeuze"
            value={chosenVersie ?? ''}
            onChange={(event) => {
              const chosen = event.target.value
              onEarlierVersie(chosen === '' ? undefined : chosen)
            }}
          >
            <option value="">{inLanguage({ nl: 'Geen', en: 'None' }, language)}</option>
            {(earlierVersies ?? []).map((versie) => (
              <option key={versie.id} value={versie.id}>
                {versieName(versie, language)}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {/* Op het raster van de kop en de kolommen, en op een telefoon met dezelfde zijmarge (opnamen van
          delen: op 390 stond de banner tegen de rand). */}
      {banner === undefined ? null : <div className="ef-formulier__banner">{banner}</div>}
      {!hasColumn ? null : (
        // Op een breed formulier verbergt de stylesheet deze knoppen en staat het paneel ernaast. Met een
        // paneel van het formulier open staat dat erbij; de knop van het adviespaneel sluit het weer.
        <div className="ef-formulier__weergave" role="group" aria-label={inLanguage({ nl: 'Weergave', en: 'View' }, language)}>
          <button type="button" className="ef-formulier__weergaveknop" aria-pressed={view === 'formulier'} onClick={() => setView('formulier')}>
            {inLanguage({ nl: 'Formulier', en: 'Form' }, language)}
          </button>
          {sidePanel === undefined ? null : (
            <button
              type="button"
              className="ef-formulier__weergaveknop"
              aria-pressed={view === 'paneel' && inColumn === undefined}
              aria-label={labelWithCount(sidePanel.knop, sidePanel.teller).naam}
              onClick={() => {
                if (inColumn !== undefined) {
                  toAdvice.current = true
                  formPanels?.onSluit({ focusTerug: false })
                }
                setView('paneel')
              }}
            >
              {labelWithCount(sidePanel.knop, sidePanel.teller).inhoud}
            </button>
          )}
          {inColumn === undefined ? null : (
            <button
              type="button"
              className="ef-formulier__weergaveknop"
              aria-pressed={view === 'paneel'}
              aria-label={labelWithCount(inColumn.knop, inColumn.teller).naam}
              onClick={() => setView('paneel')}
            >
              {labelWithCount(inColumn.knop, inColumn.teller).inhoud}
            </button>
          )}
        </div>
      )}
      {sidePanel === undefined ? null : (
        <div className="ef-sr-only" aria-live="polite" data-proef="advies-melding">
          {sidePanel.melding ?? ''}
        </div>
      )}
      <div className="ef-formulier__body" ref={bodyRef} {...(!hasColumn ? {} : { 'data-weergave': view })}>
        <TableOfContents
          secties={tocSections}
          taal={language}
          active={tocActive}
          {...(criteriaPer === undefined ? {} : { criteriaPer })}
          {...(criteriaProgress === undefined ? {} : { criteriaProgress })}
          {...(outstandingPer === undefined ? {} : { outstandingPer })}
          {...(hardChecksPer === undefined ? {} : { hardChecksPer })}
          {...(progressPer === undefined ? {} : { progressPer })}
          {...(reasons.size === 0 ? {} : { adjustmentPer })}
          rechtZichtbaar={showRight}
          genummerd={projection.nummering !== false}
          label={tableOfContentsLabel ?? inLanguage({ nl: 'Secties', en: 'Sections' }, language)}
          navRef={(element) => {
            tableOfContentsRef.current = element
          }}
          onKies={goTo}
        />
        <div className="ef-formulier__kolom">
          {contextHeader === undefined ? (
            <h1 className="ef-formulier__titel">{inLanguage(projection.titel, language)}</h1>
          ) : null}
          {outstanding === undefined || outstanding.verzoek === 0 || outstanding.open === false ? null : (
            <OutstandingOverview
              secties={sections}
              regels={outstanding.regels}
              taal={language}
              headerRef={overviewHeaderRef}
              onJump={jumpTo}
              {...(outstanding.id === undefined ? {} : { id: outstanding.id })}
              {...(outstanding.onSluiten === undefined ? {} : { onSluiten: outstanding.onSluiten })}
              {...(outstanding.fout === undefined ? {} : { fout: outstanding.fout })}
              {...(outstanding.indienen === undefined ? {} : { indienen: outstanding.indienen })}
            />
          )}
          {withLayerLists(
            mode === 'toetsen'
              ? reviewPairs(sections).map(({ lead, reviews }) => ({
                  types: [lead, ...reviews].map((section) => section.type),
                  node:
                    reviews.length === 0 ? (
                      renderSection(lead)
                    ) : (
                      // De sectie en het oordeel erover naast elkaar, zoals het ontwerp van het
                      // toetsscherm: de toetser leest links en oordeelt rechts (R-109). Op een smal
                      // formulier onder elkaar, want de rij breekt af.
                      <ReviewPair key={`paar-${lead.id}`} tekst={renderSection(lead)} oordeel={reviews.map(renderSection)} />
                    ),
                }))
              : sections.map((section) => ({ types: [section.type], node: renderSection(section) })),
            layerLists,
          )}
        </div>
        {!hasColumn ? null : (
          <div className="ef-formulier__zij">
            {inColumn === undefined || formPanels === undefined ? (
              sidePanel?.render({ actieveSectie: active, springNaar: jumpToAnchor })
            ) : (
              <PanelFrame
                paneel={inColumn}
                taal={language}
                blad={false}
                toon={formPanels.toon}
                context={{ actieveSectie: active, springNaar: jumpToAnchor }}
                onSluit={() => formPanels.onSluit()}
              />
            )}
          </div>
        )}
      </div>
      {openPanel === undefined || !phone || formPanels === undefined ? null : (
        <PanelFrame
          paneel={openPanel}
          taal={language}
          blad
          toon={formPanels.toon}
          context={{
            actieveSectie: active,
            // Vanuit het blad naar het formulier: eerst het blad dicht, anders blijft de pagina inert.
            springNaar: (target) => {
              flushSync(() => formPanels.onSluit({ focusTerug: false }))
              jumpToAnchor(target)
            },
          }}
          onSluit={() => formPanels.onSluit()}
        />
      )}
      {renderFooter?.({
        active,
        // In de gefocuste weergave van `aanvullen` gaat "volgende" naar de volgende sectie van de
        // werkverzameling, want daar werkt de invuller (R-35); het geheel blijft één klik weg.
        next: nextAfter(focused ? sections.filter(inFocus) : sections, active),
        goTo,
      })}
    </div>
  )
}

/**
 * De band boven het formulier als er iets voorop staat: hoeveel, welke secties (elk een link), en
 * de schakelaar naar het geheel (R-35, R-132, R-189, R-140).
 *
 * De secties bij naam, elk een link ernaartoe. Zonder dat zag de invuller "1 sectie vraagt
 * aanpassing" en moest hij zelf zoeken welke. Naast de statusregel en niet erin: een live region
 * met links erin leest bij elke wijziging alles opnieuw voor. Dezelfde sprong als in het overzicht
 * van openstaande controles: eerst de sectie open, dan de focus op haar kop (`focusInField`).
 */
function FocusBand({
  language,
  status,
  listLabel,
  sections,
  onJump,
  onlyWork,
  onToggle,
  narrowLabel,
}: {
  readonly language: Language
  readonly status: string
  readonly listLabel: { readonly nl: string; readonly en: string }
  readonly sections: readonly ProjectionSection[]
  readonly onJump: (sectionId: string) => void
  readonly onlyWork: boolean
  readonly onToggle: () => void
  readonly narrowLabel: { readonly nl: string; readonly en: string }
}): ReactNode {
  return (
    <div className="ef-formulier__focus">
      <p className="ef-formulier__focustekst" role="status">
        {status}
      </p>
      <ul className="ef-formulier__focuslijst" aria-label={inLanguage(listLabel, language)}>
        {sections.map((section) => (
          <li key={section.id}>
            <a
              className="ef-formulier__focuslink"
              href={`#${section.id}`}
              onClick={(event) => {
                event.preventDefault()
                onJump(section.id)
              }}
            >
              {inLanguage(section.titel, language)}
            </a>
          </li>
        ))}
      </ul>
      <button type="button" className="ef-formulier__schakelaar" aria-pressed={!onlyWork} onClick={onToggle}>
        {onlyWork ? inLanguage({ nl: 'Toon het hele formulier', en: 'Show the whole form' }, language) : inLanguage(narrowLabel, language)}
      </button>
    </div>
  )
}

/**
 * Wat er bij een hertoets voorop staat, in één zin naar de redenen (R-98, R-189).
 *
 * Naar de redenen en niet naar de regel: de renderer krijgt de regel niet, en de zin hoort te
 * zeggen wat er werkelijk staat. Bij de keuze "alleen aanpassing gevraagd" is er nooit een sectie
 * die alleen is gewijzigd, dus noemt de zin dan alleen de aanpassing.
 */
export function reviewFocusStatus(reasons: readonly WerkverzamelingReason[], language: Language): string {
  const count = reasons.length
  const requested = reasons.some((reason) => reason !== 'gewijzigd')
  const changed = reasons.some((reason) => reason !== 'openstaand-criterium')
  const what =
    requested && changed
      ? { nl: 'wat bij de vorige toets aanpassing vroeg of sindsdien is gewijzigd', en: 'what needed changes at the previous review or has changed since' }
      : requested
        ? { nl: 'wat bij de vorige toets aanpassing vroeg', en: 'what needed changes at the previous review' }
        : { nl: 'wat sinds de vorige toets is gewijzigd', en: 'what has changed since the previous review' }
  return language === 'nl'
    ? `${count} ${count === 1 ? 'sectie staat' : 'secties staan'} open: ${what.nl}. De andere secties zijn ingeklapt.`
    : `${count} ${count === 1 ? 'section is' : 'sections are'} open: ${what.en}. The other sections are collapsed.`
}

/**
 * De getekende secties (of paren) met elke laaglijst direct na het laatste item waarin een sectie
 * van die laag staat. Een laag zonder zichtbare sectie krijgt geen lijst: wie de laag niet ziet,
 * ziet haar citaties ook niet.
 */
function withLayerLists(
  items: readonly { readonly types: readonly ProjectionSection['type'][]; readonly node: ReactNode }[],
  lists: readonly { readonly laag: 'invoer' | 'toets'; readonly node: ReactNode }[],
): ReactNode[] {
  const out: ReactNode[] = []
  const lastOf = new Map<string, number>()
  items.forEach((item, place) => {
    for (const type of item.types) {
      const layer = laagOf(type)
      if (layer !== null) lastOf.set(layer, place)
    }
  })
  items.forEach((item, place) => {
    out.push(item.node)
    for (const list of lists) {
      if (lastOf.get(list.laag) === place) out.push(<Fragment key={`laaglijst-${list.laag}`}>{list.node}</Fragment>)
    }
  })
  return out
}

/**
 * Een getoetste sectie met haar oordeelkaart (ontwerp `Toets.dc.html`, R-109).
 *
 * Naast elkaar loopt de kaart mee terwijl de toetser door de sectie scrolt, zodat het oordeel bij
 * de tekst in beeld blijft. Een kaart die hoger is dan het scherm (een criterium met toelichting en
 * opmerking) pint niet alleen aan de bovenrand, want dan bleef "Oordeel vastleggen" achter de
 * actiebalk: `useFollowSticky` onthult de onderkant bij naar beneden scrollen, met de gemeten
 * hoogte van die balk als ondermarge. Onder elkaar zet de stylesheet de kaart op `static` en doet
 * de hook niets.
 */
/**
 * De lucht boven iets dat in het formulier plakt: 16px, en de compacte kop erbij zolang die bovenaan
 * staat (`CompactHeader`). Een balk in een verborgen houder (telefoon, een melding over de sessie) heeft
 * geen hoogte en telt dus niet mee.
 */
function belowCompactHeader(form: HTMLElement | null): number {
  const bar = form?.querySelector<HTMLElement>(".ef-kompaktkop[data-zichtbaar='true'] .ef-kompaktkop__balk")
  return 16 + (bar?.offsetHeight ?? 0)
}

function ReviewPair({ tekst: text, oordeel: judgement }: { readonly tekst: ReactNode; readonly oordeel: ReactNode }): ReactNode {
  const pairRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  // Onder de compacte kop als die bovenaan staat, net als de inhoudsopgave (besluit van 2 november 2026).
  const cardTop = useCallback((): number => belowCompactHeader(pairRef.current?.closest<HTMLElement>('.ef-formulier') ?? null), [])
  useFollowSticky(cardRef, pairRef, { topGap: cardTop, bottomGap: footerSpace })
  return (
    <div className="ef-toetspaar" ref={pairRef}>
      <div className="ef-toetspaar__tekst">{text}</div>
      <div className="ef-toetspaar__oordeel" ref={cardRef}>
        {judgement}
      </div>
    </div>
  )
}

/** De gemeten hoogte van de actiebalk (`--ef-voet-ruimte`) plus de kleine marge, in pixels. */
function footerSpace(): number {
  const root = document.querySelector('.ef-formulier')
  const measured = root === null ? 0 : Number.parseFloat(getComputedStyle(root).getPropertyValue('--ef-voet-ruimte'))
  return (Number.isFinite(measured) ? measured : 0) + 16
}

/**
 * Hoeveel van een element met focus minstens boven de voet moet staan voordat het vangnet het laat
 * zoals het staat: genoeg voor één regel tekst of de bovenrand van een knop. Geen ontwerpmaat maar
 * een drempel in de meting, en daarom hier en niet in de tokens.
 */
const MINIMUM_VISIBLE = 24

/** Geen nummers, voor een formulier dat de nummering uitzet (R-04). */
const NO_NUMBERS: ReadonlyMap<string, number> = new Map()

/** De sectie na de actieve in deze rij, of niets bij de laatste (of als de actieve er niet in staat). */
function nextAfter(sections: readonly ProjectionSection[], active: string | undefined): string | undefined {
  const place = sections.findIndex((section) => section.id === active)
  if (place === -1) return sections[0]?.id
  return sections[place + 1]?.id
}

/**
 * Hoe een eerdere versie in de kiezer heet.
 *
 * De gebeurtenis en de datum, want dat is waar een mens hem aan herkent: "ingediend op 20
 * augustus 2026". Het volgnummer staat erbij omdat twee overdrachten op één dag kunnen vallen en
 * de lijst dan twee gelijke regels zou hebben.
 */
export function versieName(versie: EarlierVersie, language: Language): string {
  const date = new Date(versie.moment).toLocaleDateString(localeOf(language), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
  // De gebeurtenis in woorden en niet als sleutel ("opnieuw-ingediend", audit van 30 oktober 2026);
  // een onbekende gebeurtenis valt terug op een neutraal woord en niet op de sleutel.
  const name = EVENT_NAME[versie.gebeurtenis]?.[language] ?? (language === 'nl' ? 'Versie' : 'Version')
  return language === 'nl' ? `${String(versie.volgnummer)}. ${name} op ${date}` : `${String(versie.volgnummer)}. ${name} on ${date}`
}

/** De zes gebeurtenissen die een revisie afsluiten (R-173), zoals een mens ze noemt. */
const EVENT_NAME: Readonly<Record<string, { readonly nl: string; readonly en: string }>> = {
  ingediend: { nl: 'Ingediend', en: 'Submitted' },
  teruggelegd: { nl: 'Teruggelegd', en: 'Returned' },
  aangevuld: { nl: 'Aangevuld', en: 'Supplemented' },
  'opnieuw-ingediend': { nl: 'Opnieuw ingediend', en: 'Resubmitted' },
  goedgekeurd: { nl: 'Goedgekeurd', en: 'Approved' },
  afgekeurd: { nl: 'Afgekeurd', en: 'Rejected' },
}

/**
 * Welke sectie nu in beeld is, en ernaartoe springen.
 *
 * `IntersectionObserver` en geen scroll-luisteraar: die laatste rekent bij elke
 * scrollstap over alle secties en is precies de reden dat lange formulieren
 * gaan schokken. De bovenste sectie die in beeld is, is de actieve.
 *
 * **Onderaan de pagina geldt een terugval op de laatste sectie.** De actieve band is
 * bewust een smalle lijst op een kwart van de viewport (`rootMargin` hieronder), zodat een
 * sectie pas actief wordt zodra hij daadwerkelijk gelezen wordt en niet al bij het eerste
 * randje in beeld. De laatste sectie van het formulier kan die band
 * echter nooit bereiken zodra hij korter is dan de resterende scrolruimte: er is dan
 * simpelweg niets meer om verder te scrollen. Zonder deze terugval licht de laatste sectie
 * in de inhoudsopgave dan nooit op, ook niet nadat de invuller er expliciet naartoe heeft
 * geklikt (`kies` scrollt naar de kop, maar de pagina kan niet verder omlaag om die kop
 * ook echt in de bovenste band te krijgen). Zelfde constructie als CitatenPOC's
 * `useScrollSpy` (`src/editor/useScrollSpy.ts`), de maatstaf voor controlgedrag
 * (eFormulieren/CLAUDE.md).
 *
 * Bij het kiezen uit de inhoudsopgave gaat de focus mee naar de kop van de
 * sectie. Zonder dat springt het beeld wel, maar blijft de toetsenbordgebruiker
 * achter in de inhoudsopgave staan en werkt R-142 alleen met de muis.
 */
function useActiveSection(sectionIds: readonly string[]): {
  actief: string | undefined
  register: (sectionId: string) => (element: HTMLElement | null) => void
  choose: (sectionId: string) => void
} {
  const elements = useRef(new Map<string, HTMLElement>())
  const [active, setActive] = useState<string | undefined>(sectionIds[0])
  const key = sectionIds.join(',')

  useEffect(() => {
    const inView = new Set<string>()
    let atBottom = false
    const recompute = () => {
      if (atBottom) {
        const last = sectionIds[sectionIds.length - 1]
        if (last !== undefined) setActive(last)
        return
      }
      const top = sectionIds.find((id) => inView.has(id))
      if (top !== undefined) setActive(top)
    }

    const observer = new IntersectionObserver(
      (events) => {
        for (const event of events) {
          const id = event.target.getAttribute('data-sectie')
          if (id === null) continue
          if (event.isIntersecting) inView.add(id)
          else inView.delete(id)
        }
        recompute()
      },
      // Een activeringslijn op een kwart van de hoogte en geen band vanaf de bovenrand (audit van 30
      // oktober 2026). Met de band van 0 tot 40 procent was de staart van de vorige sectie bovenin
      // nog "in beeld" en won die, ook als de kop van de volgende al ruim leesbaar was; de vorige
      // sectie bleef gemarkeerd tot haar laatste pixel de bovenrand passeerde. Nu is actief de
      // sectie die de lijst kruist. Valt de lijst tussen twee secties, dan blijft de vorige staan.
      { rootMargin: '-25% 0px -74% 0px' },
    )
    for (const element of elements.current.values()) observer.observe(element)

    const onScroll = () => {
      const document_ = document.documentElement
      const hasOverflow = document_.scrollHeight > window.innerHeight + 4
      const next = hasOverflow && window.innerHeight + window.scrollY >= document_.scrollHeight - 2
      if (next !== atBottom) {
        atBottom = next
        recompute()
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    onScroll()

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
    // sleutel en niet de array zelf: een nieuwe array met dezelfde ids hoort de
    // observer niet opnieuw op te zetten.
  }, [key, sectionIds])

  const signIn = useCallback(
    (sectionId: string) => (element: HTMLElement | null) => {
      if (element === null) elements.current.delete(sectionId)
      else {
        element.setAttribute('data-sectie', sectionId)
        elements.current.set(sectionId, element)
      }
    },
    [],
  )

  const choose = useCallback((sectionId: string) => {
    setActive(sectionId)
    const element = elements.current.get(sectionId)
    if (element === undefined) return
    const soft = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    element.scrollIntoView({ behavior: soft ? 'smooth' : 'auto', block: 'start' })
    element.querySelector<HTMLElement>('.ef-sectie__knop')?.focus()
  }, [])

  return { actief: active, register: signIn, choose }
}

/**
 * De secties in paren: een sectie met de toetssecties die er direct achter staan en erover gaan
 * (`interleaved` heeft ze al zo gezet). Een toetssectie zonder `hoortBij` staat alleen.
 */
function reviewPairs(sections: readonly ProjectionSection[]): readonly { lead: ProjectionSection; reviews: ProjectionSection[] }[] {
  const out: { lead: ProjectionSection; reviews: ProjectionSection[] }[] = []
  for (const section of sections) {
    const last = out.at(-1)
    if (section.type === 'toets' && section.hoortBij !== undefined && last?.lead.id === section.hoortBij) {
      last.reviews.push(section)
      continue
    }
    out.push({ lead: section, reviews: [] })
  }
  return out
}

/**
 * De secties met elke toetssectie direct achter de sectie waarover ze gaat (R-109, D-55).
 *
 * Een toetssectie zonder `hoortBij` blijft op zijn eigen plek staan: hij doet een uitspraak over
 * het formulier als geheel, en die hoort niet ergens tussen te worden geschoven.
 */
function interleaved(sections: readonly ProjectionSection[]): readonly ProjectionSection[] {
  const reviewsPer = new Map<string, ProjectionSection[]>()
  for (const section of sections) {
    if (section.type !== 'toets' || section.hoortBij === undefined) continue
    reviewsPer.set(section.hoortBij, [...(reviewsPer.get(section.hoortBij) ?? []), section])
  }
  if (reviewsPer.size === 0) return sections

  const placed = new Set(
    [...reviewsPer.values()].flat().map((section) => section.id),
  )
  const out: ProjectionSection[] = []
  for (const section of sections) {
    if (placed.has(section.id)) continue
    out.push(section)
    for (const review of reviewsPer.get(section.id) ?? []) out.push(review)
  }
  return out
}
